import { DataTable } from "@/components/table/table";
import { useDeleteEvent } from "@/hooks/useDeleteModal";
import { useStaffDirectoryModal } from "@/hooks/useStaffDirectoryModal";
import { useTRPC } from "@/lib/trpc";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { Button } from "../ui/button";
import { ConfirmModal } from "../ui/confirm-modal";
import { StaffDirectoryModalForm } from "./staff-directory-modal-form";
import { columns } from "./staff-directory.columns";

const staffMemberFormSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  title: z.string().min(1, "Title is required"),
  department: z.string().optional(),
  email: z.string().email().optional().or(z.literal("")),
  phone: z.string().optional(),
  officeHours: z.string().optional(),
  officeLocation: z.string().optional(),
  bio: z.string().optional(),
  sortOrder: z.number().default(0),
  isActive: z.boolean().default(true),
  photoMediaId: z.string().optional(),
});

type StaffMemberFormValues = z.infer<typeof staffMemberFormSchema>;

export function StaffDirectoryTable() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { data: staffMembers = [], isLoading } = useQuery(
    trpc.staffMembers.list.queryOptions(),
  );

  const deleteStaffMemberId = useDeleteEvent((s) => s.toDeleteId);
  const setDeleteStaffMemberId = useDeleteEvent((s) => s.setToDeleteId);

  const isStaffModalOpen = useStaffDirectoryModal((s) => s.isOpen);
  const setIsStaffModalOpen = useStaffDirectoryModal((s) => s.setIsModalOpen);
  const setStaffMemberId = useStaffDirectoryModal((s) => s.setStaffMemberId);
  const staffMemberId = useStaffDirectoryModal((s) => s.staffMemberId);
  const defaultValues = useStaffDirectoryModal((s) => s.defaultValues);
  const setDefaultValues = useStaffDirectoryModal((s) => s.setDefaultValues);

  const form = useForm<StaffMemberFormValues>({
    resolver: zodResolver(staffMemberFormSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      title: "",
      department: "",
      email: "",
      phone: "",
      officeHours: "",
      officeLocation: "",
      bio: "",
      sortOrder: 0,
      isActive: true,
      photoMediaId: undefined,
    },
  });

  useEffect(() => {
    if (defaultValues && Object.keys(defaultValues).length > 0) {
      form.reset({
        firstName: defaultValues.firstName || "",
        lastName: defaultValues.lastName || "",
        title: defaultValues.title || "",
        department: defaultValues.department || "",
        email: defaultValues.email || "",
        phone: defaultValues.phone || "",
        officeHours: defaultValues.officeHours || "",
        officeLocation: defaultValues.officeLocation || "",
        bio: defaultValues.bio || "",
        sortOrder: defaultValues.sortOrder || 0,
        isActive: defaultValues.isActive ?? true,
        photoMediaId: defaultValues.photoMediaId,
      });
    }
  }, [form, defaultValues]);

  const deleteMutation = useMutation(
    trpc.staffMembers.delete.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["staffMembers"] });
        setDeleteStaffMemberId(null);
        toast.success("Staff member deleted successfully");
      },
      onError: (error: unknown) => {
        toast.error(
          (error as Error).message || "Failed to delete staff member",
        );
      },
    }),
  );

  const createMutation = useMutation(
    trpc.staffMembers.create.mutationOptions({
      onSuccess: () => {
        form.reset();
        queryClient.invalidateQueries({ queryKey: ["staffMembers"] });
        setStaffMemberId(null);
        toast.success("Staff member created successfully");
        setIsStaffModalOpen(false);
      },
      onError: (error: unknown) => {
        toast.error(
          (error as Error).message || "Failed to create staff member",
        );
      },
    }),
  );

  const updateMutation = useMutation(
    trpc.staffMembers.update.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["staffMembers"] });
        setStaffMemberId(null);
        setIsStaffModalOpen(false);
        form.reset();
        toast.success("Staff member updated successfully");
      },
      onError: (error: unknown) => {
        toast.error(
          (error as Error).message || "Failed to update staff member",
        );
      },
    }),
  );

  const handleSubmit = (data: StaffMemberFormValues) => {
    if (staffMemberId) {
      updateMutation.mutate({ ...data, id: staffMemberId });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleNewStaffMember = () => {
    setDefaultValues({});
    form.reset({
      firstName: "",
      lastName: "",
      title: "",
      department: "",
      email: "",
      phone: "",
      officeHours: "",
      officeLocation: "",
      bio: "",
      sortOrder: 0,
      isActive: true,
      photoMediaId: undefined,
    });
    setIsStaffModalOpen(true);
  };

  return (
    <div className="flex flex-1 p-8 justify-around gap-8">
      <div className="flex flex-col space-y-8 w-full xl:w-3/4 lg:max-w-4xl xl:max-w-6xl">
        <div className="flex items-center justify-between space-y-2">
          <div>
            <h2 className="text-2xl font-bold tracking-tight">
              Staff Directory
            </h2>
            <p className="text-muted-foreground">
              Manage staff members and contact information
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <Button onClick={handleNewStaffMember}>Add Staff Member</Button>
          </div>
        </div>

        <StaffDirectoryModalForm
          isModalOpen={isStaffModalOpen}
          setIsModalOpen={setIsStaffModalOpen}
          form={form}
          onSubmit={handleSubmit}
          isLoading={createMutation.isPending || updateMutation.isPending}
        />

        <ConfirmModal
          open={Boolean(deleteStaffMemberId)}
          onConfirm={() => {
            if (deleteStaffMemberId)
              deleteMutation.mutate({ id: deleteStaffMemberId });
          }}
          onDismiss={() => {
            setDeleteStaffMemberId(null);
          }}
          isLoading={deleteMutation.isPending}
        />

        {!isLoading && (
          <DataTable
            data={staffMembers}
            columns={columns}
            filterColumn="lastName"
          />
        )}
      </div>
    </div>
  );
}
